import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Modal, Tag, Tooltip } from 'antd';
import { toast } from '@rickylandino/react-messages';
import { CheckCircle, AlertCircle } from 'lucide-react';
import Globals from '../../../config/globals';
import {
    useResultEntryFailuresCount,
    useResultEntryFailuresData,
    useDismissResultEntryFailure
} from '../../../hooks/useWidgetData';
import { useSelectedDashboardFacility } from '../../../hooks/useSelectedDashboardFacility';
import ReconcileResultEntryFailureModal from './ResultEntryFailures/ReconcileResultEntryFailureModal';

const isDark = () => Globals.theme === 'dark';

/**
 * ResultEntryFailuresWidget component
 * System-wide view of all result entry failures with Match to Exam and Dismiss actions.
 *
 * @param {Object} props
 * @param {boolean} [props.isCollapsed=false]
 * @param {boolean} [props.isExpanded=false]
 * @param {Function} [props.setIsLoading]
 * @param {Function} [props.setSharedState]
 * @param {boolean} [props.queryEnabled=true]
 */
export default function ResultEntryFailuresWidget({
    isCollapsed = false,
    isExpanded = false,
    setIsLoading,
    setSharedState,
    queryEnabled = true
}) {
    const dismissMutation = useDismissResultEntryFailure();
    const { selectedFacilities } = useSelectedDashboardFacility();

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);

    const [showReconcileModal, setShowReconcileModal] = useState(false);
    const [failureToReconcile, setFailureToReconcile] = useState(null);

    const [showDismissModal, setShowDismissModal] = useState(false);
    const [dismissTarget, setDismissTarget] = useState(null);

    const shouldLoadFullData = !isCollapsed || isExpanded;

    const { data: countData, isLoading: isLoadingCount } = useResultEntryFailuresCount(
        (isCollapsed && !isExpanded) && queryEnabled,
        selectedFacilities
    );

    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
        refetch
    } = useResultEntryFailuresData(currentPage, pageSize, { selectedFacilities }, shouldLoadFullData && queryEnabled);

    const results = data?.data || [];
    const totalCount = data?.totalCount || 0;
    const count = shouldLoadFullData ? totalCount : (countData?.count || 0);

    useEffect(() => {
        if (setIsLoading && isExpanded) setIsLoading(isLoading || isFetching);
    }, [isLoading, isFetching, isExpanded, setIsLoading]);

    useEffect(() => {
        if (shouldLoadFullData && isError) {
            toast.error('Failed to fetch result entry failures. Please try again.');
        }
    }, [shouldLoadFullData, isError, error]);

    useEffect(() => {
        if (setSharedState && refetch) setSharedState(prev => ({ ...prev, refetch }));
    }, [setSharedState, refetch]);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'numeric', day: 'numeric', year: 'numeric'
        });
    }, []);

    const handleReconcile = useCallback((row) => {
        setFailureToReconcile(row);
        setShowReconcileModal(true);
    }, []);

    const handleDismiss = useCallback((row) => {
        setDismissTarget(row);
        setShowDismissModal(true);
    }, []);

    const handleConfirmDismiss = useCallback(async () => {
        if (!dismissTarget?.resultEntryFailureId) return;
        try {
            await dismissMutation.mutateAsync(dismissTarget.resultEntryFailureId);
            setShowDismissModal(false);
            setDismissTarget(null);
            toast.success('Failure dismissed');
        } catch (e) {
            console.error('Dismiss error:', e);
            toast.error('Failed to dismiss. Please try again.');
        }
    }, [dismissTarget, dismissMutation]);

    const pink = isDark() ? '#ec4899' : '#db2777';
    const pinkHover = isDark() ? '#db2777' : '#be185d';

    const reportStateBadge = useCallback((state) => {
        if (state === 'F') return <Tag color="blue" style={{ marginRight: 0 }}>Final</Tag>;
        if (state === 'A') return <Tag color="purple" style={{ marginRight: 0 }}>Amendment</Tag>;
        return (
            <Tooltip title="Unrecognised report state — dismiss this failure">
                <Tag color="warning" style={{ marginRight: 0 }}>{state || 'Unknown'}</Tag>
            </Tooltip>
        );
    }, []);

    const getMissingFields = useCallback((row) => {
        return [
            (!row.reportState || !['F', 'A'].includes(row.reportState)) && 'report state',
            !row.facilityName && 'facility',
            (!row.patientName && !row.patientMrn) && 'patient identity',
        ].filter(Boolean);
    }, []);

    const columns = useMemo(() => [
        {
            title: 'Report State',
            dataIndex: 'reportState',
            key: 'reportState',
            width: 110,
            render: (val, row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {reportStateBadge(val)}
                    {getMissingFields(row).length > 0 && (
                        <Tooltip title={`Missing: ${getMissingFields(row).join(', ')} — dismiss instead`}>
                            <AlertCircle size={14} style={{ color: isDark() ? '#fb923c' : '#ea580c', flexShrink: 0 }} />
                        </Tooltip>
                    )}
                </div>
            )
        },
        {
            title: 'Accession #',
            dataIndex: 'accessionNumber',
            key: 'accessionNumber',
            width: 140,
            render: (val) => val || '—'
        },
        {
            title: 'Patient',
            key: 'patient',
            width: 160,
            render: (_, row) => (
                <div>
                    <div>{row.patientName || '—'}</div>
                    {row.patientMrn && (
                        <div className="text-xs" style={{ color: isDark() ? '#a1a1aa' : '#71717a' }}>
                            MRN: {row.patientMrn}
                        </div>
                    )}
                </div>
            )
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facilityName',
            width: 150,
            render: (val) => val || <span style={{ color: isDark() ? '#a1a1aa' : '#71717a', fontStyle: 'italic' }}>Unknown</span>
        },
        {
            title: 'Exam Type',
            dataIndex: 'examTypeName',
            key: 'examTypeName',
            width: 160,
            render: (val) => val || '—'
        },
        {
            title: 'Radiologist',
            dataIndex: 'radiologistName',
            key: 'radiologistName',
            width: 150,
            render: (val) => val || '—'
        },
        {
            title: 'BIRADs',
            dataIndex: 'birads',
            key: 'birads',
            width: 80,
            render: (val) => val ?? '—'
        },
        {
            title: 'Exam Date',
            dataIndex: 'examDateTime',
            key: 'examDateTime',
            width: 110,
            render: (d) => formatDate(d)
        },
        {
            title: 'Result Date',
            dataIndex: 'resultDate',
            key: 'resultDate',
            width: 110,
            render: (d) => formatDate(d)
        },
        {
            title: 'Failure Reason',
            dataIndex: 'failureReason',
            key: 'failureReason',
            width: 220,
            render: (val) => val || '—'
        },
        {
            title: 'Status Code',
            dataIndex: 'statusCode',
            key: 'statusCode',
            width: 100,
            render: (val) => val ?? '—'
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 235,
            fixed: 'right',
            render: (_, row) => {
                const missing = getMissingFields(row);
                const unactionable = missing.length > 0;
                return (
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Tooltip title={unactionable ? `Missing: ${missing.join(', ')}` : undefined}>
                            <span style={unactionable ? { display: 'inline-block', cursor: 'not-allowed' } : undefined}>
                                <button
                                    onClick={(e) => { e.stopPropagation(); if (!unactionable) handleReconcile(row); }}
                                    disabled={unactionable}
                                    style={{
                                        backgroundColor: unactionable ? (isDark() ? '#3f3f46' : '#e4e4e7') : pink,
                                        color: unactionable ? (isDark() ? '#71717a' : '#a1a1aa') : 'white',
                                        border: 'none',
                                        cursor: unactionable ? 'not-allowed' : 'pointer',
                                        opacity: unactionable ? 0.65 : 1,
                                        padding: '6px 12px',
                                        borderRadius: '4px',
                                        fontWeight: 500,
                                        fontSize: '0.875rem',
                                        pointerEvents: unactionable ? 'none' : 'auto'
                                    }}
                                    onMouseEnter={e => { if (!unactionable) e.currentTarget.style.backgroundColor = pinkHover; }}
                                    onMouseLeave={e => { if (!unactionable) e.currentTarget.style.backgroundColor = pink; }}
                                >
                                    Match to Exam
                                </button>
                            </span>
                        </Tooltip>
                        <button
                            onClick={(e) => { e.stopPropagation(); handleDismiss(row); }}
                            className="px-3 py-1.5 rounded-lg font-medium text-sm"
                            style={{
                                backgroundColor: 'transparent',
                                color: isDark() ? '#a1a1aa' : '#71717a',
                                border: `1px solid ${isDark() ? '#52525b' : '#d1d5db'}`,
                                cursor: 'pointer'
                            }}
                        >
                            Dismiss
                        </button>
                    </div>
                );
            }
        }
    ], [formatDate, handleReconcile, handleDismiss, reportStateBadge, getMissingFields, pink, pinkHover]);

    // COLLAPSED STATE
    if (isCollapsed && !isExpanded) {
        if (isLoadingCount) {
            return (
                <div className="flex items-center justify-center py-4">
                    <div
                        className="animate-spin rounded-full h-8 w-8 border-b-2"
                        style={{ borderColor: isDark() ? '#a1a1aa' : '#71717a' }}
                    />
                </div>
            );
        }
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <div className="text-5xl font-light mb-2" style={{ color: pink }}>
                    {count.toLocaleString()}
                </div>
                <div className="text-sm" style={{ color: isDark() ? '#a1a1aa' : '#71717a' }}>
                    {count === 1 ? 'result entry failure' : 'result entry failures'}
                </div>
            </div>
        );
    }

    // EMPTY STATE
    if (!isLoading && count === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-4">
                <CheckCircle className="h-16 w-16 mx-auto mb-4" style={{ color: pink }} />
                <p style={{ color: pink }}>No Result Entry Failures Found</p>
            </div>
        );
    }

    // EXPANDED STATE
    return (
        <>
            <Table
                columns={columns}
                dataSource={results}
                rowKey="resultEntryFailureId"
                pagination={{
                    current: currentPage,
                    pageSize,
                    total: totalCount,
                    onChange: (page, newPageSize) => {
                        setCurrentPage(page);
                        if (newPageSize !== pageSize) {
                            setPageSize(newPageSize);
                            setCurrentPage(1);
                        }
                    },
                    pageSizeOptions: ['25', '50', '100', '200'],
                    showSizeChanger: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} failures`
                }}
                size="small"
                scroll={{ y: 500 }}
                virtual
                loading={{ spinning: (isLoading && !isExpanded) || isFetching }}
            />

            <ReconcileResultEntryFailureModal
                visible={showReconcileModal}
                failure={failureToReconcile}
                onSuccess={() => {
                    setShowReconcileModal(false);
                    setFailureToReconcile(null);
                }}
                onCancel={() => {
                    setShowReconcileModal(false);
                    setFailureToReconcile(null);
                }}
            />

            <Modal
                visible={showDismissModal}
                title="Dismiss Result Entry Failure"
                onCancel={() => { setShowDismissModal(false); setDismissTarget(null); }}
                footer={[
                    <button
                        key="cancel"
                        onClick={() => { setShowDismissModal(false); setDismissTarget(null); }}
                        className="px-4 py-1.5 rounded-lg font-medium text-sm"
                        style={{
                            backgroundColor: 'transparent',
                            border: `1px solid ${isDark() ? '#52525b' : '#d1d5db'}`,
                            cursor: 'pointer',
                            marginRight: 8
                        }}
                    >
                        Cancel
                    </button>,
                    <button
                        key="confirm"
                        onClick={handleConfirmDismiss}
                        disabled={dismissMutation.isLoading}
                        className="px-4 py-1.5 rounded-lg font-medium text-sm"
                        style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', cursor: 'pointer' }}
                    >
                        Dismiss
                    </button>
                ]}
            >
                <p>
                    Dismissing{dismissTarget?.accessionNumber ? <> accession <strong>{dismissTarget.accessionNumber}</strong></> : ' this failure'} will
                    remove it from the queue without updating any exam data.
                </p>
                <p>Are you sure?</p>
            </Modal>
        </>
    );
}
